import type { AccAccount, AccAccountTreeNode } from './useData';

export type TreeListRow = AccAccountTreeNode & {
  depth: number;
  hasChildren: boolean;
};

export const flattenAccountTree = (
  nodes: AccAccountTreeNode[],
  collapsedIds: Set<number>,
  depth = 0,
): TreeListRow[] => {
  const rows: TreeListRow[] = [];
  for (const node of nodes) {
    const hasChildren = (node.children?.length ?? 0) > 0;
    rows.push({ ...node, depth, hasChildren });
    if (hasChildren && !collapsedIds.has(node.id)) {
      rows.push(...flattenAccountTree(node.children, collapsedIds, depth + 1));
    }
  }
  return rows;
};

export type CoaListKindFilter = 'all' | 'headers' | 'ledger' | 'inactive';

const walkFilterTree = (
  nodes: AccAccountTreeNode[],
  selfMatch: (node: AccAccountTreeNode) => boolean,
): AccAccountTreeNode[] => {
  const walk = (node: AccAccountTreeNode): AccAccountTreeNode | null => {
    const filteredChildren = (node.children ?? [])
      .map(walk)
      .filter((child): child is AccAccountTreeNode => child != null);
    if (selfMatch(node) || filteredChildren.length > 0) {
      return { ...node, children: filteredChildren };
    }
    return null;
  };
  return nodes.map(walk).filter((node): node is AccAccountTreeNode => node != null);
};

export const filterAccountTree = (nodes: AccAccountTreeNode[], term: string): AccAccountTreeNode[] => {
  const q = term.trim().toLowerCase();
  if (!q) return nodes;
  return walkFilterTree(
    nodes,
    (node) =>
      node.code.toLowerCase().includes(q) ||
      node.name.toLowerCase().includes(q) ||
      (node.department?.code?.toLowerCase().includes(q) ?? false) ||
      (node.department?.name?.toLowerCase().includes(q) ?? false),
  );
};

/** Keep ancestors so matching nodes stay in tree context. */
export const filterAccountTreeByKind = (
  nodes: AccAccountTreeNode[],
  kind: CoaListKindFilter,
): AccAccountTreeNode[] => {
  if (kind === 'all') return nodes;
  return walkFilterTree(nodes, (node) => {
    if (kind === 'headers') return !node.isPosting;
    if (kind === 'ledger') return node.isPosting;
    return !node.isActive;
  });
};

export const accountHasChildren = (
  item: AccAccount & { hasChildren?: boolean; children?: AccAccountTreeNode[] },
): boolean => {
  if (item.hasChildren === true) return true;
  if ((item.children?.length ?? 0) > 0) return true;
  return (item._count?.children ?? 0) > 0;
};

export const accountIsUsedInRecords = (item: AccAccount): boolean =>
  item.usedInRecords === true || (item._count?.documentLines ?? 0) > 0;

export const accountCanDelete = (
  item: AccAccount & { hasChildren?: boolean; children?: AccAccountTreeNode[] },
): boolean => !accountHasChildren(item) && !accountIsUsedInRecords(item);

/** IDs of nodes that have children (for collapse-all). */
export const collectExpandableNodeIds = (nodes: AccAccountTreeNode[]): number[] => {
  const ids: number[] = [];
  for (const node of nodes) {
    if ((node.children?.length ?? 0) > 0) {
      ids.push(node.id);
      ids.push(...collectExpandableNodeIds(node.children));
    }
  }
  return ids;
};

/** Collapse descendants only — roots stay expanded so the first level is visible. */
export const collectCollapsedIdsBelowRoot = (nodes: AccAccountTreeNode[]): number[] => {
  const ids: number[] = [];
  for (const root of nodes) {
    ids.push(...collectExpandableNodeIds(root.children ?? []));
  }
  return ids;
};

/** Header (non-posting) nodes only — used as the left-side group tree. */
export const buildHeaderTree = (nodes: AccAccountTreeNode[]): AccAccountTreeNode[] => {
  const walk = (node: AccAccountTreeNode): AccAccountTreeNode | null => {
    if (node.isPosting) return null;
    const children = (node.children ?? [])
      .map(walk)
      .filter((child): child is AccAccountTreeNode => child != null);
    return { ...node, children };
  };
  return nodes.map(walk).filter((node): node is AccAccountTreeNode => node != null);
};

export const findAccountNodeById = (
  nodes: AccAccountTreeNode[],
  id: number,
): AccAccountTreeNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findAccountNodeById(node.children ?? [], id);
    if (found) return found;
  }
  return null;
};

/** Ledger accounts under a group (direct children only). */
export const getDirectLedgerAccounts = (node: AccAccountTreeNode | null): AccAccountTreeNode[] => {
  if (!node) return [];
  return (node.children ?? []).filter((child) => child.isPosting);
};

/** All ledger accounts under a group (any depth). */
export const getDescendantLedgerAccounts = (node: AccAccountTreeNode | null): AccAccountTreeNode[] => {
  if (!node) return [];
  const result: AccAccountTreeNode[] = [];
  const walk = (n: AccAccountTreeNode) => {
    for (const child of n.children ?? []) {
      if (child.isPosting) result.push(child);
      else walk(child);
    }
  };
  walk(node);
  return result;
};

export const countLedgerAccounts = (node: AccAccountTreeNode): number =>
  getDescendantLedgerAccounts(node).length;

export const filterAccountList = (accounts: AccAccountTreeNode[], term: string): AccAccountTreeNode[] => {
  const q = term.trim().toLowerCase();
  if (!q) return accounts;
  return accounts.filter(
    (node) =>
      node.code.toLowerCase().includes(q) ||
      node.name.toLowerCase().includes(q) ||
      (node.combLevel?.toLowerCase().includes(q) ?? false) ||
      (node.department?.code?.toLowerCase().includes(q) ?? false) ||
      (node.department?.name?.toLowerCase().includes(q) ?? false),
  );
};

/** Path from root to node (inclusive), or empty if not found. */
export const getAccountPath = (
  nodes: AccAccountTreeNode[],
  id: number,
  trail: AccAccountTreeNode[] = [],
): AccAccountTreeNode[] => {
  for (const node of nodes) {
    const next = [...trail, node];
    if (node.id === id) return next;
    const found = getAccountPath(node.children ?? [], id, next);
    if (found.length) return found;
  }
  return [];
};

/** Expandable descendant ids of a single node (for expand/collapse branch). */
export const collectExpandableDescendantIds = (node: AccAccountTreeNode): number[] =>
  collectExpandableNodeIds(node.children ?? []);

export const nodeDepthInTree = (nodes: AccAccountTreeNode[], id: number): number => {
  const path = getAccountPath(nodes, id);
  return path.length > 0 ? path.length - 1 : 0;
};
